import { Badge } from "@shared/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Progress } from "@shared/ui/progress";
import { Activity, AlertCircle, Calendar } from "lucide-react";

export const ViolationsSamplingSection: React.FC<{ data: any }> = ({ data }) => {
  const getStatusVariant = (status: string) => {
    switch (status) {
      case "compliant":
      case "on-track":
      case "resolved":
        return "outline";
      case "warning":
      case "behind":
      case "investigating":
        return "secondary";
      case "violation":
      case "overdue":
      case "critical":
      case "active":
        return "destructive";
      default:
        return "outline";
    }
  };

  return (
    <div className="grid grid-cols-2 gap-6">
      {/* Active Violations */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center space-x-2 text-base">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <span>Active Violations</span>
            <Badge variant="destructive" className="text-xs">
              {data?.activeViolations?.length || 0}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="space-y-3">
            {data?.activeViolations?.map((violation, index) => (
              <div
                key={index}
                className="p-3 rounded border border-red-200 bg-red-50"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <AlertCircle className="h-4 w-4 text-red-600" />
                    <p className="font-medium text-sm">
                      {violation.violationType}
                    </p>
                  </div>
                  <Badge variant="destructive" className="text-xs">
                    {violation.severity?.toUpperCase()}
                  </Badge>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">
                    Parameter: {violation.parameter}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Detected: {violation.detectedDate}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-red-600 font-medium">
                      Due: {violation.dueDate}
                    </span>
                    <Badge variant="outline" className="text-xs">
                      {violation.status?.toUpperCase()}
                    </Badge>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Sampling Schedule */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center space-x-2 text-base">
            <Calendar className="h-5 w-5 text-green-600" />
            <span>Sampling Schedule</span>
            <Badge variant="outline" className="text-xs">
              {data?.samplingSchedule?.length || 0} tests
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="space-y-3">
            {data?.samplingSchedule?.map((sample, index) => (
              <div key={index} className="p-3 rounded border">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <Activity className="h-4 w-4 text-blue-600" />
                    <p className="font-medium text-sm">{sample.category}</p>
                  </div>
                  <Badge
                    variant={getStatusVariant(sample.status)}
                    className="text-xs"
                  >
                    {sample.status?.toUpperCase()}
                  </Badge>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">
                      Progress
                    </span>
                    <span className="text-xs font-medium">
                      {sample.progress || 0}%
                    </span>
                  </div>
                  <Progress
                    value={sample.progress || 0}
                    className="h-2"
                  />
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">
                      Frequency: {sample.frequency}
                    </span>
                    <span className="text-muted-foreground">
                      Next: {sample.nextDue}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
